/**
 * The app frame: the one element that owns the height and the theme.
 *
 * Its CSS lives in `index.css` (`#app-frame`, `.app-scroll`): exactly the
 * viewport tall, never scrolling itself, with one inner region that scrolls
 * and pads clear of the safe areas.
 *
 * The theme preference lives in `stores/prefs.ts`, which is the only reader
 * of the stored value in `src`. It lands as `data-theme` on the frame IN THE
 * FIRST RENDER — the store hydrates synchronously at creation, so no commit
 * ever shows the other palette — and is mirrored on `<html>`, so the body
 * behind the frame, the page's `color-scheme` and Base UI's portals (which
 * render into `<body>`, outside the frame) agree with it.
 *
 * Before any module runs, the inline script in `index.html` has already set
 * `data-theme` on `<html>` from the same key, so a stored choice that differs
 * from the system does not show one blank frame in the system colour.
 */
import { type ReactNode, useCallback, useLayoutEffect } from "react";
import { statusBarStyleFor, themeColorFromCanvas } from "./app/system-chrome";
import { onAppActiveChange, setStatusBarTheme } from "./native";
import {
  type ResolvedTheme,
  type ThemePreference,
  usePrefs,
  useResolvedTheme,
} from "./stores/prefs";

export type { ResolvedTheme, ThemePreference } from "./stores/prefs";

export function useThemePreference(): {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (next: ThemePreference) => void;
} {
  const preference = usePrefs((state) => state.theme);
  const setTheme = usePrefs((state) => state.setTheme);
  const resolved = useResolvedTheme();
  const setPreference = useCallback(
    (next: ThemePreference) => setTheme(next),
    [setTheme]
  );
  return { preference, resolved, setPreference };
}

/**
 * The chrome OUTSIDE the WebView follows the theme the app resolved, not the
 * one the phone is in: the Android system bars, and on the web the address bar
 * and the task-switcher card. Both are read from `--color-canvas` / the
 * resolved theme, so a person who picked "dark" on a light phone gets dark
 * chrome.
 *
 * Fire-and-forget, always: a plugin that is missing or rejects must never
 * block a paint, and on the web `setStatusBarTheme` is a documented no-op.
 * Called AFTER `data-theme` lands on `<html>`, because the token it reads is
 * the one that attribute selects.
 */
function applySystemChrome(resolved: ResolvedTheme): void {
  setStatusBarTheme(statusBarStyleFor(resolved)).catch(() => undefined);
  const color = themeColorFromCanvas();
  if (!color) {
    return;
  }
  // T113 injects one tag per scheme; both are rewritten, so the media query
  // stops deciding the moment the app has an opinion.
  for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
    meta.setAttribute("content", color);
  }
}

export function AppFrame({ children }: { children: ReactNode }) {
  const resolved = useResolvedTheme();
  // Before paint, so `<html>` never shows one frame of the other theme.
  useLayoutEffect(() => {
    document.documentElement.setAttribute("data-theme", resolved);
    applySystemChrome(resolved);
    // Both bar plugins re-apply their OWN remembered style on a configuration
    // change, and the manifest routes `uiMode` to the running activity — so a
    // phone toggled to dark resets the bars even when `resolved` did not move
    // (the preference is not "system"). Re-apply on both signals that follow
    // such a change.
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const reapply = () => applySystemChrome(resolved);
    media.addEventListener("change", reapply);
    const stopWatchingApp = onAppActiveChange((active) => {
      if (active) {
        applySystemChrome(resolved);
      }
    });
    return () => {
      media.removeEventListener("change", reapply);
      stopWatchingApp();
    };
  }, [resolved]);
  return (
    <div data-theme={resolved} id="app-frame">
      <div className="app-scroll">{children}</div>
      {/* Opaque backings for the system bars (`index.css`, `.system-bar`). */}
      <div aria-hidden="true" className="system-bar system-bar-top" />
      <div aria-hidden="true" className="system-bar system-bar-bottom" />
    </div>
  );
}
